import React, { useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Alert, Box, Collapse, Paper, Stack, Typography } from '@mui/material';

import ScheduleDoctorIssueSection from './ScheduleDoctorIssueSection';
import { handleExpandRowKeyDown } from './campaignScheduleTabLayout';
import { groupViolations } from './campaignScheduleViolationFormatter';
import type { CampaignScheduleImportViolation } from './types';

interface Props {
  violations: Array<CampaignScheduleImportViolation>;
}

// See README.md#violation-display-rules.
function headline(
  rules: Array<unknown>,
  genuineIssues: Array<unknown>,
): { severity: 'success' | 'warning' | 'info'; text: string } {
  if (genuineIssues.length > 0) {
    return {
      severity: 'warning',
      text: `${genuineIssues.length} issue${genuineIssues.length === 1 ? '' : 's'} detected`,
    };
  }
  if (rules.length > 0) {
    return { severity: 'info', text: `${rules.length} rule${rules.length === 1 ? '' : 's'}, no issues` };
  }
  return { severity: 'success', text: 'No issues detected' };
}

export default function ScheduleDoctor({ violations }: Props) {
  const [expanded, setExpanded] = useState(false);
  const grouped = groupViolations(violations);
  const rules = grouped.filter((violation) => violation.severity === 'info');
  const genuineIssues = grouped.filter((violation) => violation.severity !== 'info');
  const hasIssues = grouped.length > 0;
  const { severity: headlineSeverity, text: headlineText } = headline(rules, genuineIssues);
  const toggle = () => setExpanded((current) => !current);

  return (
    <Paper sx={{ bgcolor: 'background.paper', borderRadius: 3 }} variant="outlined">
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        role={hasIssues ? 'button' : undefined}
        spacing={2}
        sx={{ cursor: hasIssues ? 'pointer' : 'default', px: 3, py: 2 }}
        tabIndex={hasIssues ? 0 : undefined}
        onClick={() => hasIssues && toggle()}
        onKeyDown={hasIssues ? (event) => handleExpandRowKeyDown(event, toggle) : undefined}
      >
        <Stack alignItems="center" direction="row" spacing={1}>
          {expanded ? (
            <KeyboardArrowDownIcon color={hasIssues ? undefined : 'disabled'} fontSize="small" />
          ) : (
            <KeyboardArrowRightIcon color={hasIssues ? undefined : 'disabled'} fontSize="small" />
          )}
          <Typography fontWeight={700} variant="h3">
            Schedule Doctor
          </Typography>
        </Stack>
        <Alert severity={headlineSeverity} sx={{ py: 0 }}>
          {headlineText}
        </Alert>
      </Stack>

      {hasIssues && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ px: 3, pb: 2 }}>
            <ScheduleDoctorIssueSection defaultExpanded issues={genuineIssues} itemLabel="issue" title="Issues" />
            <ScheduleDoctorIssueSection defaultExpanded={false} issues={rules} itemLabel="rule" title="Rules" />
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}
